/*
 * Release hero slider: targeted performance/lifecycle patch.
 * Compatible with both the original and patched SwiperComponent in global.js.
 * Replaces assets/hero-slider.js ONLY. Does not change Swiper or image assets.
 */
if (!customElements.get('hero-slider')) {
  class HeroSlider extends SwiperComponent {
    constructor() {
      super();
      this.lastActiveIndex = null;
      this._heroConnected = false;
      this._animationTimeout = null;
      this._lastAnimatedSlide = null;
      this._handleBlockSelect = this.handleBlockSelect.bind(this);
      this._handleKeydown = this.handleKeydown.bind(this);
      this._handleEditorSection = this.handleEditorSection.bind(this);
    }

    connectedCallback() {
      if (this._heroConnected) return;
      this._heroConnected = true;
      this.section = this.closest('.shopify-section') || this.closest('section');
      this.swiperOptions = this.buildHeroSliderOptions();

      document.addEventListener('keydown', this._handleKeydown);
      window.addEventListener('shopify:block:select', this._handleBlockSelect);

      // Keep editor sections immediately interactive and reinitializable.
      if (window.Shopify && Shopify.designMode) {
        window.addEventListener('shopify:section:load', this._handleEditorSection);
        window.addEventListener('shopify:section:select', this._handleEditorSection);
        this.init();
        return;
      }

      // Only defer offscreen heroes. A first-screen hero must work immediately,
      // without waiting for every image/app on the page to finish loading.
      if (!this.section || !('IntersectionObserver' in window)) {
        this.init();
        return;
      }
      const bounds = this.section.getBoundingClientRect();
      if (bounds.top < window.innerHeight + 350 && bounds.bottom > -350) {
        this.init();
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer?.disconnect();
        this.observer = null;
        if (this.isConnected) this.init();
      }, { rootMargin: '350px 0px' });
      this.observer.observe(this.section);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this._handleKeydown);
      window.removeEventListener('shopify:block:select', this._handleBlockSelect);
      window.removeEventListener('shopify:section:load', this._handleEditorSection);
      window.removeEventListener('shopify:section:select', this._handleEditorSection);
      this.observer?.disconnect();
      this.observer = null;
      clearTimeout(this._animationTimeout);
      this._animationTimeout = null;
      // Never leave a slide's text hidden if Shopify removes the section
      // during an entrance animation.
      this.querySelectorAll('.hero__animation.in-delay').forEach(el => {
        el.classList.remove('in-delay');
      });
      this._lastAnimatedSlide = null;
      if (this.swiper && !this.swiper.destroyed) this.swiper.destroy(true, true);
      this.swiper = null;
      this.slider = null;
      this.swiperInitialized = false;
      this.lastActiveIndex = null;
      this._heroConnected = false;
    }

    buildHeroSliderOptions() {
      const autoplayInterval = Number(this.dataset.autoplayInterval) || 5000;
      const nextButton = this.querySelector('.swiper-button--next');
      const prevButton = this.querySelector('.swiper-button--prev');
      const pagination = this.querySelector('.swiper-pagination');

      return {
        rewind: true,
        slidesPerView: 1,
        speed: 600,
        followFinger: false,
        navigation: nextButton && prevButton ? {
          nextEl: nextButton,
          prevEl: prevButton
        } : false,
        pagination: pagination ? {
          el: pagination,
          clickable: true,
          renderBullet(i, className) {
            return `
              <button class="${className}">
                <span>0${i + 1}</span>
                <svg class="square-progress" width="26" height="26">
                  <rect class="square-origin" width="26" height="26" rx="5" ry="5" />
                </svg>
                <svg class="progress" width="1.8rem" height="1.8rem" style="inset-inline-start: ${0 - (i * 2.4 + 3.4)}rem">
                  <circle class="circle-origin" r="0.8rem" cx="0.95rem" cy="0.95rem"></circle>
                </svg>
              </button>`;
          }
        } : false,
        autoplay: this.dataset.autoplay === 'true' ? { delay: autoplayInterval } : false,
        on: {
          init: swiper => this.handleSlideChange(swiper),
          slideChange: swiper => this.handleSlideChange(swiper)
        }
      };
    }

    init() {
      if (this.swiperInitialized) return;
      if (typeof Swiper === 'undefined') return;
      this.swiperInitialized = true;
      try {
        this.swiperOptions = this.buildHeroSliderOptions();
        this.swiper = new Swiper(this, this.swiperOptions);
        this.slider = this.swiper; // Existing theme compatibility
      } catch (error) {
        this.swiperInitialized = false;
        throw error;
      }
    }

    handleEditorSection(event) {
      if (!event.target?.contains(this)) return;
      if (this.swiper && !this.swiper.destroyed) this.swiper.update();
      else this.init();
    }

    handleBlockSelect(event) {
      if (!this.swiper || this.swiper.destroyed) return;
      if (event.target?.closest('hero-slider') !== this) return;
      const index = Number(event.target.dataset.index);
      if (Number.isInteger(index) && index >= 0 && index < this.swiper.slides.length) {
        this.swiper.slideTo(index, 600);
      }
    }

    handleSlideChange(swiper) {
      this.handleSlideChangeAnimations(swiper);
      const activeSlide = swiper.slides?.[swiper.activeIndex];
      const activeInner = activeSlide?.querySelector('.hero__inner');
      // Do not let an offscreen hero overwrite the visible hero's header colour.
      if (activeInner && document.querySelector('.header__inner') && this.isInViewport()) {
        const color = activeInner.dataset.headerMenuTextColor;
        if (color) document.documentElement.style.setProperty('--transparent-header-menu-text-color', color);
      }
    }

    handleSlideChangeAnimations(swiper) {
      const activeIndex = swiper.activeIndex;
      const previousIndex = this.lastActiveIndex;
      this.lastActiveIndex = activeIndex;
      clearTimeout(this._animationTimeout);
      this._animationTimeout = null;

      // A rapid swipe/click can cancel the previous slide's timer.
      // Unhide its text now, otherwise it can remain hidden indefinitely.
      if (this._lastAnimatedSlide) {
        this._lastAnimatedSlide.querySelectorAll('.hero__animation.in-delay').forEach(el => {
          el.classList.remove('in-delay');
        });
        this._lastAnimatedSlide = null;
      }

      const activeSlide = swiper.slides?.[activeIndex];
      if (!activeSlide) return;
      const items = [...activeSlide.querySelectorAll('.hero__animation')];
      if (!items.length) return;

      // Preserve the existing mobile experience (without entrance delays).
      if (typeof DeviceDetector !== 'undefined' && DeviceDetector.isMobile()) {
        items.forEach(item => item.classList.remove('in-delay'));
        return;
      }

      items.forEach(item => item.classList.add('in-delay'));
      this._lastAnimatedSlide = activeSlide;
      let delay = 300;
      if (previousIndex !== null && previousIndex !== undefined) {
        if (previousIndex > activeIndex) delay += 650;
        if (previousIndex < activeIndex) delay += 1000;
        if (previousIndex !== activeIndex) {
          swiper.wrapperEl.style.transitionDuration = '1000ms';
          swiper.wrapperEl.style.transitionTimingFunction = 'cubic-bezier(0.45, 0.00, 0.15, 0.95)';
        }
      }
      this._animationTimeout = setTimeout(() => {
        items.forEach(item => item.classList.remove('in-delay'));
        this._lastAnimatedSlide = null;
        this._animationTimeout = null;
      }, delay);
    }

    handleKeydown(event) {
      if (!this.swiper || this.swiper.destroyed || !this.isInViewport()) return;
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target;
      if (target instanceof Element && target.closest('input, textarea, select, [contenteditable="true"]')) return;
      // Do not also move the hero while a customer operates a menu, cart,
      // product carousel or another focused component using arrow keys.
      const focused = document.activeElement;
      if (focused && focused !== document.body && !this.contains(focused)) return;
      if (event.key === 'ArrowRight') this.querySelector('.swiper-button--next')?.click();
      else if (event.key === 'ArrowLeft') this.querySelector('.swiper-button--prev')?.click();
    }

    isInViewport() {
      const rect = this.getBoundingClientRect();
      return rect.top < innerHeight && rect.bottom > 0 && rect.left < innerWidth && rect.right > 0;
    }
  }
  customElements.define('hero-slider', HeroSlider);
}
